import {useState} from "react";
import {Link, useParams} from "react-router-dom";
import {PhoneHQLoader} from 'ch3-ui-lib';
import {Bell, Check, Clock, LucideIcon, XCircle} from "lucide-react";
import {useGetEnsStatsQuery, useListEnsAlertsQuery} from "../../../store/features/ens/ensService";
import {EnsAlertStatus, EnsAlertSummary, EnsAlertType} from "../../../store/features/ens/types";
import {StatusBadge} from "../components/StatusBadge";
import {TypeBadge} from "../components/TypeBadge";
import {LevelBadge} from "../components/LevelBadge";
import {ConfirmedProgress} from "../components/ConfirmedProgress";
import {TargetCell} from "../components/TargetCell";
import {useEnsGroupNames} from "../useEnsGroupNames";
import {ENS_ALERT_REFRESH_INTERVAL_MS} from "../utils";

const STATS_DAYS = 30;
const LIST_LIMIT = 50;
const MIN_LEVELS = 4;

const COLUMNS = 'grid-cols-[minmax(220px,1fr)_92px_130px_120px_minmax(150px,200px)_140px_116px]';
const RESPONSIVE_COLUMNS = 'md:grid-cols-[minmax(220px,1fr)_92px_130px_120px_minmax(150px,200px)_140px_116px]';

const STATUS_FILTERS: { value: EnsAlertStatus | 'ALL'; label: string }[] = [
    {value: 'ALL', label: 'All'},
    {value: 'PENDING', label: 'Pending'},
    {value: 'ESCALATING', label: 'Escalating'},
    {value: 'CONFIRMED', label: 'Confirmed'},
    {value: 'FAILED', label: 'Failed'},
    {value: 'CANCELLED', label: 'Cancelled'},
];

const TYPE_FILTERS: { value: EnsAlertType | 'ALL'; label: string }[] = [
    {value: 'ALL', label: 'All'},
    {value: 'FRAUD', label: 'Fraud'},
    {value: 'TASK', label: 'Task'},
    {value: 'POLICY', label: 'Policy'},
    {value: 'CUSTOM', label: 'Custom'},
];

const shortPublicId = (publicId: string) =>
    publicId.length > 8 ? `${publicId.slice(0, 8)}…` : publicId;

const formatPercent = (rate: number) =>
    `${Math.round(rate * 100)}%`;

const formatTime = (iso: string) => {
    try {
        return new Date(iso).toLocaleTimeString([], {hour: '2-digit', minute: '2-digit'});
    } catch {
        return '—';
    }
};

const formatDate = (iso: string) => {
    try {
        return new Date(iso).toLocaleDateString([], {year: 'numeric', month: '2-digit', day: '2-digit'});
    } catch {
        return '';
    }
};

const KpiCard = ({label, value, subLabel, icon: IconCmp, iconClassName}: {
    label: string;
    value: string | number;
    subLabel?: string;
    icon?: LucideIcon;
    iconClassName?: string;
}) => (
    <div className="border border-line rounded bg-surface p-4">
        <div className="mb-2 flex items-center justify-between gap-2">
            <span className="text-[11px] font-semibold uppercase text-ink-muted">{label}</span>
            {IconCmp && <IconCmp size={18} className={`opacity-70 ${iconClassName ?? ''}`}/>}
        </div>
        <div className="mb-1 text-2xl font-semibold leading-none text-ink">{value}</div>
        {subLabel && <div className="text-xs text-ink-muted">{subLabel}</div>}
    </div>
);

const FilterPill = ({active, label, onClick}: { active: boolean; label: string; onClick: () => void }) => (
    <button
        type="button"
        onClick={onClick}
        className={`h-7 rounded-full px-3 text-xs font-semibold transition-colors ${
            active ? 'bg-indigo-600 text-white' : 'text-zinc-500 hover:bg-zinc-2@00 hover:text-zinc-900'
        }`}
    >
        {label}
    </button>
);

const CurrentLevel = ({sequence}: { sequence: number | null }) => {
    const count = Math.max(MIN_LEVELS, sequence ?? 0);
    return (
        <div className="flex flex-wrap items-center gap-1">
            {Array.from({length: count}, (_, i) => i + 1).map((seq) => (
                <LevelBadge key={seq} sequenceNo={seq} active={seq === sequence} size="sm"/>
            ))}
        </div>
    );
};

const AlertRow = ({alert, selected, groupNames}: {
    alert: EnsAlertSummary;
    selected: boolean;
    groupNames: Map<number, string>;
}) => (
    <Link
        to={`/admin/ens/alerts/${alert.publicId}`}
        aria-current={selected ? 'true' : undefined}
        className={`grid grid-cols-1 gap-2 ${RESPONSIVE_COLUMNS} items-start border-t border-line px-4 py-3 text-sm md:gap-0 md:items-center ${
            selected ? 'bg-indigo-50' : 'hover:bg-zinc-100'
        }`}
    >
        <div className="min-w-0 md:pr-4" title={alert.message}>
            <div className="truncate font-semibold text-ink">{alert.name}</div>
            <div className="font-mono text-[11px] text-ink-muted" title={alert.publicId}>
                {shortPublicId(alert.publicId)}
            </div>
        </div>
        <div className="flex min-w-0 items-center justify-between gap-3 md:block">
            <span className="text-[11px] font-semibold uppercase text-ink-muted md:hidden">Type</span>
            <TypeBadge type={alert.type}/>
        </div>
        <div className="flex min-w-0 items-center justify-between gap-3 md:block md:pr-4">
            <span className="text-[11px] font-semibold uppercase text-ink-muted md:hidden">Target</span>
            <TargetCell targetGroupIds={alert.targetGroupIds} targetCount={alert.targetCount} groupNames={groupNames}/>
        </div>
        <div className="flex min-w-0 items-center justify-between gap-3 md:block">
            <span className="text-[11px] font-semibold uppercase text-ink-muted md:hidden">Status</span>
            <StatusBadge status={alert.status} variant="dot"/>
        </div>
        <div className="flex min-w-0 items-center justify-between gap-3 md:block md:pr-4">
            <span className="text-[11px] font-semibold uppercase text-ink-muted md:hidden">Confirmed</span>
            <ConfirmedProgress
                confirmed={alert.confirmedCount}
                target={alert.targetCount}
                failed={alert.failedCount}
                pending={alert.pendingCount}
                compact
            />
        </div>
        <div className="flex min-w-0 items-center justify-between gap-3 md:block">
            <span className="text-[11px] font-semibold uppercase text-ink-muted md:hidden">Current level</span>
            <CurrentLevel sequence={alert.currentLevelSequence}/>
        </div>
        <div className="flex items-center justify-between gap-3 text-[11px] text-ink-muted md:block">
            <span className="font-semibold uppercase text-ink-muted md:hidden">Created</span>
            <div className="text-right md:text-left">
                <div>{formatTime(alert.triggeredAt)}</div>
                <div className="text-ink-muted">{formatDate(alert.triggeredAt)}</div>
            </div>
        </div>
    </Link>
);

export const EnsOverview = () => {
    const {publicId: selectedId = null} = useParams<{ publicId?: string }>();
    const [statusFilter, setStatusFilter] = useState<EnsAlertStatus | 'ALL'>('ALL');
    const [typeFilter, setTypeFilter] = useState<EnsAlertType | 'ALL'>('ALL');

    const groupNames = useEnsGroupNames();
    const {data: stats, isLoading: statsLoading} = useGetEnsStatsQuery(
        {days: STATS_DAYS},
        {pollingInterval: ENS_ALERT_REFRESH_INTERVAL_MS},
    );
    const {data: page, isLoading: listLoading} = useListEnsAlertsQuery({
        offset: 0,
        limit: LIST_LIMIT,
        statuses: statusFilter === 'ALL' ? undefined : [statusFilter],
        types: typeFilter === 'ALL' ? undefined : [typeFilter],
    }, {pollingInterval: ENS_ALERT_REFRESH_INTERVAL_MS});

    if (statsLoading || listLoading) {
        return (
            <div className="flex items-center justify-center py-16">
                <PhoneHQLoader/>
            </div>
        );
    }

    const alerts = page?.data ?? [];
    const total = page?.totalCount ?? 0;

    return (
        <div className="py-6 flex flex-col gap-6">
            <div className="grid grid-cols-2 gap-3 lg:grid-cols-4 px-5">
                <KpiCard
                    label="Alerts today"
                    value={stats?.alertsToday ?? 0}
                    subLabel="Triggered today"
                    icon={Bell}
                    iconClassName="text-indigo-500"
                />
                <KpiCard
                    label="Confirmation rate"
                    value={stats ? formatPercent(stats.confirmationRate) : '0%'}
                    subLabel={`Last ${stats?.periodDays ?? STATS_DAYS} days`}
                    icon={Check}
                    iconClassName="text-success-ink"
                />
                <KpiCard
                    label="Pending"
                    value={stats?.pendingCount ?? 0}
                    subLabel="Awaiting confirmation"
                    icon={Clock}
                    iconClassName="text-warning-ink"
                />
                <KpiCard
                    label="Failed"
                    value={stats?.failedCount ?? 0}
                    subLabel="No confirmation received"
                    icon={XCircle}
                    iconClassName="text-danger-ink"
                />
            </div>

            <div className="flex flex-wrap items-center gap-2 px-5 border border-y py-2.5">
                <span className="mr-1 text-xs font-semibold text-ink-muted">Status</span>
                {STATUS_FILTERS.map((option) => (
                    <FilterPill
                        key={option.value}
                        active={statusFilter === option.value}
                        label={option.label}
                        onClick={() => setStatusFilter(option.value)}
                    />
                ))}
                <span className="mx-1 h-4 w-px bg-zinc-400"/>
                <span className="mr-1 text-xs font-semibold text-ink-muted">Type</span>
                {TYPE_FILTERS.map((option) => (
                    <FilterPill
                        key={option.value}
                        active={typeFilter === option.value}
                        label={option.label}
                        onClick={() => setTypeFilter(option.value)}
                    />
                ))}
            </div>

            <div className="overflow-hidden rounded border border-line bg-surface">
                {alerts.length === 0 && (
                    <div className="px-4 py-12 text-center text-ink-muted text-sm">
                        No alerts match the current filters.
                    </div>
                )}
                {alerts.length > 0 && (
                    <div className="overflow-x-auto">
                        <div className="md:min-w-[960px]">
                            <div className={`hidden ${COLUMNS} bg-surface-muted px-4 py-2 text-[11px] font-semibold uppercase text-ink-muted md:grid`}>
                                <div>Alert</div>
                                <div>Type</div>
                                <div>Target</div>
                                <div>Status</div>
                                <div>Confirmed</div>
                                <div>Current level</div>
                                <div>Created</div>
                            </div>
                            {alerts.map((alert) => (
                                <AlertRow key={alert.publicId} alert={alert} selected={alert.publicId === selectedId} groupNames={groupNames}/>
                            ))}
                            {total > alerts.length && (
                                <div className="border-t border-line px-4 py-2 text-center text-[11px] text-ink-muted">
                                    Showing {alerts.length} of {total} alerts
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};
